// components/common/Modal/InputDialog.tsx
import { useState, useEffect, ReactElement, KeyboardEvent, ChangeEvent } from 'react';
import { Box, TextField } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { Modal } from './Modal';

type InputDialogVariant = 'primary' | 'danger' | 'warning';

interface VariantConfig {
  color: string;
}

const variantConfig: Record<InputDialogVariant, VariantConfig> = {
  danger: { color: '#FF3366' },
  warning: { color: '#FFE600' },
  primary: { color: '#00FFD4' },
};

export interface InputDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (value: string) => void;
  onClear?: () => void;
  title?: string;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  type?: string;
  confirmText?: string;
  cancelText?: string;
  clearText?: string;
  variant?: InputDialogVariant;
  loading?: boolean;
  helperText?: string;
}

const buttonBaseStyle: SxProps<Theme> = {
  px: 3,
  py: 1,
  fontFamily: '"Rajdhani", sans-serif',
  fontWeight: 600,
  fontSize: '0.875rem',
  cursor: 'pointer',
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
};

/**
 * InputDialog - Styled input prompt dialog
 */
export function InputDialog({
  open,
  onClose,
  onSubmit,
  onClear,
  title = 'Enter Value',
  label = 'Value',
  placeholder = '',
  defaultValue = '',
  type = 'text',
  confirmText = 'OK',
  cancelText = 'Cancel',
  clearText = 'Clear',
  variant = 'primary',
  loading = false,
  helperText = '',
}: InputDialogProps): ReactElement {
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    if (open) {
      setValue(defaultValue);
    }
  }, [open, defaultValue]);

  const handleSubmit = () => {
    onSubmit(value);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const config = variantConfig[variant];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      titleColor={config.color}
      maxWidth="sm"
      showCloseButton={false}
      actions={
        <>
          <Box
            component="button"
            onClick={onClose}
            disabled={loading}
            sx={{
              ...buttonBaseStyle,
              backgroundColor: 'transparent',
              border: 'none',
              color: '#8892A0',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)', color: '#FFFFFF' },
            }}
          >
            {cancelText}
          </Box>
          {onClear && (
            <Box
              component="button"
              onClick={onClear}
              disabled={loading}
              sx={{
                ...buttonBaseStyle,
                backgroundColor: 'rgba(255, 51, 102, 0.15)',
                border: '1px solid #FF3366',
                color: '#FF3366',
                '&:hover': { backgroundColor: 'rgba(255, 51, 102, 0.25)' },
              }}
            >
              {clearText}
            </Box>
          )}
          <Box
            component="button"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              ...buttonBaseStyle,
              backgroundColor: 'rgba(0, 255, 212, 0.15)',
              border: '1px solid #00FFD4',
              color: '#00FFD4',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(0, 255, 212, 0.25)',
                boxShadow: '0 4px 15px rgba(0, 255, 212, 0.4)',
              },
            }}
          >
            {loading ? 'Processing...' : confirmText}
          </Box>
        </>
      }
    >
      <TextField
        autoFocus
        fullWidth
        label={label}
        placeholder={placeholder}
        type={type}
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        helperText={helperText}
        InputLabelProps={{ shrink: true }}
        sx={{
          mt: 2,
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#252D3A',
            color: '#FFFFFF',
            height: 56,
            '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
            '&.Mui-focused fieldset': { borderColor: '#00FFD4' },
          },
          '& .MuiInputLabel-root': {
            color: '#8892A0',
            backgroundColor: '#1E2530',
            px: 1,
          },
          '& .MuiInputLabel-root.Mui-focused': { color: '#00FFD4' },
          '& .MuiFormHelperText-root': { color: '#8892A0' },
        }}
      />
    </Modal>
  );
}

export default InputDialog;
